import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card, CardBody, Table, Thead, Tbody, Tr, Th, Td, TableContainer,
  Badge, HStack, IconButton, Button, useDisclosure, useToast,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, FormControl, FormLabel, Input, Select, Switch, VStack,
} from '@chakra-ui/react';
import { AddIcon, EditIcon, DeleteIcon } from '@chakra-ui/icons';
import PageShell from '../components/PageShell';
import ConfirmModal from '../components/ConfirmModal';
import taxRateService from '../services/taxRateService';

const emptyForm = { name: '', rate: '', description: '', applies_to: 'room', is_active: true };

const TaxRateManagement = () => {
  const { t } = useTranslation();
  const [taxRates, setTaxRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const deleteDisclosure = useDisclosure();
  const toast = useToast();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await taxRateService.getAll();
      setTaxRates(data?.results || data || []);
    } catch { setError(t('taxRates.errors.loadFailed')); }
    finally { setLoading(false); }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(emptyForm); setEditingId(null); onOpen(); };
  const openEdit = (tr) => {
    setForm({ name: tr.name, rate: tr.rate, description: tr.description || '', applies_to: tr.applies_to || 'room', is_active: tr.is_active });
    setEditingId(tr.id); onOpen();
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingId) await taxRateService.update(editingId, form);
      else {
        const hotelId = localStorage.getItem('admin_hotel_id');
        await taxRateService.create({ ...form, hotel: hotelId ? Number(hotelId) : undefined });
      }
      toast({ title: editingId ? t('taxRates.toasts.updated') : t('taxRates.toasts.created'), status: 'success', duration: 2000 });
      onClose(); load();
    } catch (err) {
      toast({ title: t('taxRates.toasts.error'), description: JSON.stringify(err.response?.data), status: 'error', duration: 4000 });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await taxRateService.remove(deleteTarget.id);
      toast({ title: t('taxRates.toasts.deleted'), status: 'success', duration: 2000 });
      deleteDisclosure.onClose(); load();
    } catch { toast({ title: t('taxRates.toasts.deleteFailed'), status: 'error', duration: 3000 }); }
  };

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  return (
    <PageShell
      title={t('taxRates.title')}
      subtitle={t('taxRates.count', { count: taxRates.length })}
      breadcrumbs={[{ label: t('taxRates.title') }]}
      loading={loading} error={error}
      actions={<Button leftIcon={<AddIcon />} colorScheme="blue" size="sm" onClick={openCreate}>{t('taxRates.buttons.add')}</Button>}
    >
      <Card shadow="sm" borderWidth="1px">
        <CardBody p={0}>
          <TableContainer>
            <Table size="sm">
              <Thead><Tr><Th>{t('taxRates.fields.name')}</Th><Th>{t('taxRates.fields.rate')}</Th><Th>{t('taxRates.fields.appliesTo')}</Th><Th>{t('taxRates.fields.description')}</Th><Th>{t('taxRates.columns.status')}</Th><Th>{t('taxRates.columns.actions')}</Th></Tr></Thead>
              <Tbody>
                {taxRates.map(tr => (
                  <Tr key={tr.id}>
                    <Td fontWeight="500" fontSize="sm">{tr.name}</Td>
                    <Td fontSize="sm">{tr.rate}%</Td>
                    <Td><Badge fontSize="xs">{tr.applies_to}</Badge></Td>
                    <Td fontSize="sm" color="gray.500">{tr.description}</Td>
                    <Td><Badge colorScheme={tr.is_active ? 'green' : 'red'} fontSize="xs">{tr.is_active ? t('taxRates.status.active') : t('taxRates.status.inactive')}</Badge></Td>
                    <Td>
                      <HStack spacing={1}>
                        <IconButton size="xs" icon={<EditIcon />} variant="ghost" onClick={() => openEdit(tr)} aria-label={t('taxRates.buttons.edit')} />
                        <IconButton size="xs" icon={<DeleteIcon />} variant="ghost" colorScheme="red"
                          onClick={() => { setDeleteTarget(tr); deleteDisclosure.onOpen(); }} aria-label={t('taxRates.buttons.delete')} />
                      </HStack>
                    </Td>
                  </Tr>
                ))}
                {taxRates.length === 0 && <Tr><Td colSpan={6} textAlign="center" py={8} color="gray.400">{t('taxRates.emptyState')}</Td></Tr>}
              </Tbody>
            </Table>
          </TableContainer>
        </CardBody>
      </Card>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingId ? t('taxRates.modal.editTitle') : t('taxRates.buttons.add')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired><FormLabel fontSize="sm">{t('taxRates.fields.name')}</FormLabel><Input size="sm" value={form.name} onChange={set('name')} placeholder={t('taxRates.fields.namePlaceholder')} /></FormControl>
              <HStack w="100%" spacing={4}>
                <FormControl isRequired><FormLabel fontSize="sm">{t('taxRates.fields.rate')}</FormLabel><Input size="sm" type="number" step="0.01" value={form.rate} onChange={set('rate')} /></FormControl>
                <FormControl><FormLabel fontSize="sm">{t('taxRates.fields.appliesTo')}</FormLabel>
                  <Select size="sm" value={form.applies_to} onChange={set('applies_to')}>
                    <option value="room">{t('taxRates.appliesTo.room')}</option><option value="food_beverage">{t('taxRates.appliesTo.food_beverage')}</option>
                    <option value="spa">{t('taxRates.appliesTo.spa')}</option><option value="all">{t('taxRates.appliesTo.all')}</option>
                  </Select>
                </FormControl>
              </HStack>
              <FormControl><FormLabel fontSize="sm">{t('taxRates.fields.description')}</FormLabel><Input size="sm" value={form.description} onChange={set('description')} /></FormControl>
              <FormControl display="flex" alignItems="center">
                <FormLabel fontSize="sm" mb={0}>{t('taxRates.status.active')}</FormLabel>
                <Switch isChecked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>{t('taxRates.buttons.cancel')}</Button>
            <Button colorScheme="blue" onClick={handleSave} isLoading={saving}>{editingId ? t('taxRates.buttons.save') : t('taxRates.buttons.create')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ConfirmModal isOpen={deleteDisclosure.isOpen} onClose={deleteDisclosure.onClose}
        onConfirm={handleDelete} title={t('taxRates.deleteModal.title')} message={t('taxRates.deleteModal.message', { name: deleteTarget?.name })} />
    </PageShell>
  );
};

export default TaxRateManagement;
